import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AnimatedButton from "./AnimatedButton";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

interface SubscribeButtonProps {
  planSlug: "free" | "basic";
  className?: string;
  children?: React.ReactNode;
}

const SubscribeButton: React.FC<SubscribeButtonProps> = ({ 
  planSlug, 
  className, 
  children 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    logger.info("SubscribeButton", "Subscribe button clicked", { planSlug, hasUser: !!user });

    // Handle free plan - just redirect to signup if not logged in
    if (planSlug === "free") {
      if (!user) {
        logger.info("SubscribeButton", "Free plan selected, redirecting to signup");
        navigate("/signup");
        return;
      } else {
        logger.info("SubscribeButton", "User already logged in with free plan");
        toast.info("Você já tem acesso ao plano gratuito");
        return;
      }
    }

    // For paid plans, require authentication
    if (!user) {
      logger.info("SubscribeButton", "User not authenticated, redirecting to signup");
      navigate("/signup");
      return;
    }

    setLoading(true);
    
    try {
      logger.info("SubscribeButton", "Calling create-checkout function", { 
        planSlug,
        userId: user.id,
        userEmail: user.email 
      });

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { 
          plan_slug: planSlug 
        },
      });

      logger.info("SubscribeButton", "create-checkout response received", { 
        hasData: !!data, 
        hasError: !!error,
        error: error?.message,
        data: data 
      });

      if (error) {
        logger.error("SubscribeButton", "create-checkout error", error);
        
        // Tratamento específico de erros
        if (error.message?.includes("STRIPE_SECRET_KEY")) {
          toast.error("Erro de configuração", {
            description: "Sistema de pagamento não configurado. Entre em contato com o suporte."
          });
        } else if (error.message?.includes("authentication") || error.message?.includes("autenticação")) {
          toast.error("Erro de autenticação", {
            description: "Faça login novamente e tente outra vez."
          });
          navigate("/login");
        } else if (error.message?.includes("Plano não encontrado")) {
          toast.error("Plano indisponível", {
            description: "Este plano não está disponível no momento."
          });
        } else {
          toast.error("Erro no pagamento", {
            description: error.message || "Não foi possível iniciar o pagamento. Tente novamente."
          });
        }
        return;
      }

      const checkoutUrl = data?.url;
      if (!checkoutUrl) {
        logger.error("SubscribeButton", "No checkout URL received", { data });
        toast.error("Erro no checkout", {
          description: "URL de pagamento não foi gerada. Tente novamente."
        });
        return;
      }

      logger.info("SubscribeButton", "Redirecting to Stripe checkout", { url: checkoutUrl });
      
      // Abrir Stripe Checkout em nova aba
      window.open(checkoutUrl, "_blank");
      
      toast.success("Redirecionando para o checkout...", {
        description: "Uma nova aba foi aberta com o formulário de pagamento."
      });

    } catch (error) {
      logger.error("SubscribeButton", "Exception in handleSubscribe", error);
      
      if (error instanceof Error) {
        if (error.message.includes("Failed to fetch")) {
          toast.error("Erro de conexão", {
            description: "Verifique sua conexão com a internet e tente novamente."
          });
        } else if (error.message.includes("timeout")) {
          toast.error("Tempo limite excedido", {
            description: "O servidor demorou para responder. Tente novamente."
          });
        } else {
          toast.error("Erro inesperado", {
            description: "Ocorreu um problema. Tente novamente em alguns instantes."
          });
        }
      } else {
        toast.error("Erro ao processar assinatura", {
          description: "Tente novamente em alguns instantes."
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedButton 
      onClick={handleSubscribe} 
      className={className} 
      disabled={loading}
    >
      {loading ? "Processando..." : children}
    </AnimatedButton>
  );
};

export default SubscribeButton;
